import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl } from "../../../Baseurl";
import { apiAdapter } from "../../../Utility/secureChannel";
// Reusing the shared axiosInstance ONLY to trigger a token refresh when this
// instance's own calls 401 — it already has the auth-flow skip logic,
// refresh-queue, and markTokenIssued() bookkeeping built in.
import axiosInstance from "./AxiosInstance";
import { markTokenIssued, isTokenLikelyExpired } from "./TokenFreshness";
import { rejectIfTabSessionStale } from "../../../Utility/tabSession";

// -----------------------------------------------------------------------
// A separate, minimal axios instance JUST for endpoints that expect plain,
// human-readable query params (like Swagger shows) — e.g. Subscription
// APIs such as online-wallet-ledger and consumed-payouts.
//
// Auth here is cookie-based (see axiosInstance.js — withCredentials: true,
// no Authorization header), so we just need withCredentials on this
// instance too — the browser sends the session cookie automatically.
// Same secure-channel adapter as axiosInstance, so these calls are tunnelled too.
const plainAxios = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
  adapter: apiAdapter, // secure channel + duplicate-submit guard
});

// ---------------------------------------------------------------------------
// Access token only lives ~60s (see AxiosInstance.jsx). This instance shares
// the same lastTokenIssuedAt tracking (via ./TokenFreshness) so it can do the
// same proactive pre-check axiosInstance does, plus it handles the reactive
// case as a safety net: if a call still 401s, refresh once (via axiosInstance,
// which already knows how) and retry this same call exactly once.
// ---------------------------------------------------------------------------
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function onRefreshed() {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
}

plainAxios.interceptors.request.use(async (config) => {
  if (isRefreshing) {
    await new Promise((resolve) => subscribeTokenRefresh(resolve));
  } else if (isTokenLikelyExpired()) {
    isRefreshing = true;
    try {
      await axiosInstance.post("Auth/RefreshToken");
      markTokenIssued();
    } catch {
      // Yahan fail hone do — asli request bhi 401 degi, jo neeche wala
      // reactive interceptor already handle karta hai
    } finally {
      isRefreshing = false;
      onRefreshed();
    }
  }
  return config;
});

plainAxios.interceptors.request.use(rejectIfTabSessionStale);

plainAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => resolve(plainAxios(originalRequest)));
        });
      }

      isRefreshing = true;
      try {
        // Auth/RefreshToken is on axiosInstance's own auth-flow allowlist,
        // so it skips the proactive check there — this just reuses that
        // existing, working refresh call.
        await axiosInstance.post("Auth/RefreshToken");
        isRefreshing = false;
        onRefreshed();
        return plainAxios(originalRequest);
      } catch (refreshError) {
        // Session truly expired — same fate as axiosInstance's own handling
        isRefreshing = false;
        refreshSubscribers = [];
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// -----------------------------------------------------------------------
// Keyed by request `key` (falls back to `url`) so multiple plain POST calls
// on the same screen (ledger, consumed-payouts, etc.) each keep their own
// data/status/error instead of overwriting one shared slot.
const initialState = {
  byKey: {}, // { [key]: { data, status: 'idle'|'loading'|'succeeded'|'failed', error } }
};

// Usage: dispatch(getPlainRequest({ url: "Subscription/online-wallet-ledger",
//   params: { EmployeeId, ForMonth, ForYear }, key: "onlineWalletLedger" }))
export const getPlainRequest = createAsyncThunk(
  "getPlainRequest/fetch",
  async ({ url, params, key }) => {
    try {
      // Backend switched these Subscription endpoints from GET to POST —
      // params still travel the same way (plain query string), only the
      // HTTP verb changed.
      const response = await plainAxios.post(url, null, {
        headers: { Accept: "*/*" },
        params: params || {},
      });
      return { key: key || url, data: response && response.data };
    } catch (error) {
      console.error("Error in getPlainRequest:", error);
      throw error;
    }
  },
);

const getPlainRequestSlice = createSlice({
  name: "getPlainRequest",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPlainRequest.pending, (state, action) => {
        const key = action.meta.arg.key || action.meta.arg.url;
        state.byKey[key] = {
          ...(state.byKey[key] || {}),
          status: "loading",
          error: null,
        };
      })
      .addCase(getPlainRequest.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.byKey[key] = { data, status: "succeeded", error: null };
      })
      .addCase(getPlainRequest.rejected, (state, action) => {
        const key = action.meta.arg.key || action.meta.arg.url;
        state.byKey[key] = {
          ...(state.byKey[key] || {}),
          status: "failed",
          error: action.error.message,
        };
      });
  },
});

export default getPlainRequestSlice.reducer;